import { createNode } from '@glueapp/graph-types';
import { OktaIntegration } from './OktaIntegration';

export const GeneratedNodes_Okta = [
    createNode({
        id: 'Okta: List Applications',
        name: 'Okta: List Applications',
        description:
            'Enumerates apps added to your organization with pagination. A subset of apps can be returned that match a supported filter expression or query.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            q: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
            filter: { kind: 'string' },
            expand: { kind: 'string' },
            includeNonDeleted: { kind: 'boolean' },
        },
        optionalInPorts: ['q', 'after', 'limit', 'filter', 'expand', 'includeNonDeleted'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        accessibility: {
                            kind: 'object',
                            properties: {
                                errorRedirectUrl: { kind: 'string' },
                                loginRedirectUrl: { kind: 'string' },
                                selfService: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        credentials: {
                            kind: 'object',
                            properties: {
                                signing: {
                                    kind: 'object',
                                    properties: {
                                        kid: { kind: 'string' },
                                        lastRotated: { kind: 'string' },
                                        nextRotation: { kind: 'string' },
                                        rotationMode: { kind: 'string' },
                                        use: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                userNameTemplate: {
                                    kind: 'object',
                                    properties: {
                                        pushStatus: { kind: 'string' },
                                        suffix: { kind: 'string' },
                                        template: { kind: 'string' },
                                        type: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        features: { kind: 'array', subType: { kind: 'string' } },
                        id: { kind: 'string' },
                        label: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        licensing: {
                            kind: 'object',
                            properties: { seatCount: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        name: { kind: 'string' },
                        profile: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        settings: {
                            kind: 'object',
                            properties: {
                                app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                implicitAssignment: { kind: 'boolean' },
                                inlineHookId: { kind: 'string' },
                                notes: {
                                    kind: 'object',
                                    properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                notifications: {
                                    kind: 'object',
                                    properties: {
                                        vpn: {
                                            kind: 'object',
                                            properties: {
                                                helpUrl: { kind: 'string' },
                                                message: { kind: 'string' },
                                                network: {
                                                    kind: 'object',
                                                    properties: {
                                                        connection: { kind: 'string' },
                                                        exclude: { kind: 'array', subType: { kind: 'string' } },
                                                        include: { kind: 'array', subType: { kind: 'string' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        signOnMode: { kind: 'string' },
                        status: { kind: 'string' },
                        visibility: {
                            kind: 'object',
                            properties: {
                                appLinks: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                autoLaunch: { kind: 'boolean' },
                                autoSubmitToolbar: { kind: 'boolean' },
                                hide: {
                                    kind: 'object',
                                    properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            after: input.inPorts.after,
                            limit: input.inPorts.limit,
                            filter: input.inPorts.filter,
                            expand: input.inPorts.expand,
                            includeNonDeleted: input.inPorts.includeNonDeleted,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Add Application',
        name: 'Okta: Add Application',
        description: 'Adds a new application to your Okta organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            activate: { kind: 'boolean' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    accessibility: {
                        kind: 'object',
                        properties: {
                            errorRedirectUrl: { kind: 'string' },
                            loginRedirectUrl: { kind: 'string' },
                            selfService: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            signing: {
                                kind: 'object',
                                properties: {
                                    kid: { kind: 'string' },
                                    lastRotated: { kind: 'string' },
                                    nextRotation: { kind: 'string' },
                                    rotationMode: { kind: 'string' },
                                    use: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userNameTemplate: {
                                kind: 'object',
                                properties: {
                                    pushStatus: { kind: 'string' },
                                    suffix: { kind: 'string' },
                                    template: { kind: 'string' },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    features: { kind: 'array', subType: { kind: 'string' } },
                    id: { kind: 'string' },
                    label: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    licensing: {
                        kind: 'object',
                        properties: { seatCount: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    settings: {
                        kind: 'object',
                        properties: {
                            app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            implicitAssignment: { kind: 'boolean' },
                            inlineHookId: { kind: 'string' },
                            notes: {
                                kind: 'object',
                                properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            notifications: {
                                kind: 'object',
                                properties: {
                                    vpn: {
                                        kind: 'object',
                                        properties: {
                                            helpUrl: { kind: 'string' },
                                            message: { kind: 'string' },
                                            network: {
                                                kind: 'object',
                                                properties: {
                                                    connection: { kind: 'string' },
                                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                                    include: { kind: 'array', subType: { kind: 'string' } },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    signOnMode: { kind: 'string' },
                    status: { kind: 'string' },
                    visibility: {
                        kind: 'object',
                        properties: {
                            appLinks: {
                                kind: 'object',
                                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            autoLaunch: { kind: 'boolean' },
                            autoSubmitToolbar: { kind: 'boolean' },
                            hide: {
                                kind: 'object',
                                properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['activate', 'body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    accessibility: {
                        kind: 'object',
                        properties: {
                            errorRedirectUrl: { kind: 'string' },
                            loginRedirectUrl: { kind: 'string' },
                            selfService: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            signing: {
                                kind: 'object',
                                properties: {
                                    kid: { kind: 'string' },
                                    lastRotated: { kind: 'string' },
                                    nextRotation: { kind: 'string' },
                                    rotationMode: { kind: 'string' },
                                    use: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userNameTemplate: {
                                kind: 'object',
                                properties: {
                                    pushStatus: { kind: 'string' },
                                    suffix: { kind: 'string' },
                                    template: { kind: 'string' },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    features: { kind: 'array', subType: { kind: 'string' } },
                    id: { kind: 'string' },
                    label: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    licensing: {
                        kind: 'object',
                        properties: { seatCount: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    settings: {
                        kind: 'object',
                        properties: {
                            app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            implicitAssignment: { kind: 'boolean' },
                            inlineHookId: { kind: 'string' },
                            notes: {
                                kind: 'object',
                                properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            notifications: {
                                kind: 'object',
                                properties: {
                                    vpn: {
                                        kind: 'object',
                                        properties: {
                                            helpUrl: { kind: 'string' },
                                            message: { kind: 'string' },
                                            network: {
                                                kind: 'object',
                                                properties: {
                                                    connection: { kind: 'string' },
                                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                                    include: { kind: 'array', subType: { kind: 'string' } },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    signOnMode: { kind: 'string' },
                    status: { kind: 'string' },
                    visibility: {
                        kind: 'object',
                        properties: {
                            appLinks: {
                                kind: 'object',
                                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            autoLaunch: { kind: 'boolean' },
                            autoSubmitToolbar: { kind: 'boolean' },
                            hide: {
                                kind: 'object',
                                properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ activate: input.inPorts.activate }).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get Application',
        name: 'Okta: Get Application',
        description: 'Fetches an application from your Okta organization by `id`.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['expand'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    accessibility: {
                        kind: 'object',
                        properties: {
                            errorRedirectUrl: { kind: 'string' },
                            loginRedirectUrl: { kind: 'string' },
                            selfService: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            signing: {
                                kind: 'object',
                                properties: {
                                    kid: { kind: 'string' },
                                    lastRotated: { kind: 'string' },
                                    nextRotation: { kind: 'string' },
                                    rotationMode: { kind: 'string' },
                                    use: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userNameTemplate: {
                                kind: 'object',
                                properties: {
                                    pushStatus: { kind: 'string' },
                                    suffix: { kind: 'string' },
                                    template: { kind: 'string' },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    features: { kind: 'array', subType: { kind: 'string' } },
                    id: { kind: 'string' },
                    label: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    licensing: {
                        kind: 'object',
                        properties: { seatCount: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    settings: {
                        kind: 'object',
                        properties: {
                            app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            implicitAssignment: { kind: 'boolean' },
                            inlineHookId: { kind: 'string' },
                            notes: {
                                kind: 'object',
                                properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            notifications: {
                                kind: 'object',
                                properties: {
                                    vpn: {
                                        kind: 'object',
                                        properties: {
                                            helpUrl: { kind: 'string' },
                                            message: { kind: 'string' },
                                            network: {
                                                kind: 'object',
                                                properties: {
                                                    connection: { kind: 'string' },
                                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                                    include: { kind: 'array', subType: { kind: 'string' } },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    signOnMode: { kind: 'string' },
                    status: { kind: 'string' },
                    visibility: {
                        kind: 'object',
                        properties: {
                            appLinks: {
                                kind: 'object',
                                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            autoLaunch: { kind: 'boolean' },
                            autoSubmitToolbar: { kind: 'boolean' },
                            hide: {
                                kind: 'object',
                                properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ expand: input.inPorts.expand }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Update Application',
        name: 'Okta: Update Application',
        description: 'Updates an application in your organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    accessibility: {
                        kind: 'object',
                        properties: {
                            errorRedirectUrl: { kind: 'string' },
                            loginRedirectUrl: { kind: 'string' },
                            selfService: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            signing: {
                                kind: 'object',
                                properties: {
                                    kid: { kind: 'string' },
                                    lastRotated: { kind: 'string' },
                                    nextRotation: { kind: 'string' },
                                    rotationMode: { kind: 'string' },
                                    use: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userNameTemplate: {
                                kind: 'object',
                                properties: {
                                    pushStatus: { kind: 'string' },
                                    suffix: { kind: 'string' },
                                    template: { kind: 'string' },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    features: { kind: 'array', subType: { kind: 'string' } },
                    id: { kind: 'string' },
                    label: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    licensing: {
                        kind: 'object',
                        properties: { seatCount: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    settings: {
                        kind: 'object',
                        properties: {
                            app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            implicitAssignment: { kind: 'boolean' },
                            inlineHookId: { kind: 'string' },
                            notes: {
                                kind: 'object',
                                properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            notifications: {
                                kind: 'object',
                                properties: {
                                    vpn: {
                                        kind: 'object',
                                        properties: {
                                            helpUrl: { kind: 'string' },
                                            message: { kind: 'string' },
                                            network: {
                                                kind: 'object',
                                                properties: {
                                                    connection: { kind: 'string' },
                                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                                    include: { kind: 'array', subType: { kind: 'string' } },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    signOnMode: { kind: 'string' },
                    status: { kind: 'string' },
                    visibility: {
                        kind: 'object',
                        properties: {
                            appLinks: {
                                kind: 'object',
                                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            autoLaunch: { kind: 'boolean' },
                            autoSubmitToolbar: { kind: 'boolean' },
                            hide: {
                                kind: 'object',
                                properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    accessibility: {
                        kind: 'object',
                        properties: {
                            errorRedirectUrl: { kind: 'string' },
                            loginRedirectUrl: { kind: 'string' },
                            selfService: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            signing: {
                                kind: 'object',
                                properties: {
                                    kid: { kind: 'string' },
                                    lastRotated: { kind: 'string' },
                                    nextRotation: { kind: 'string' },
                                    rotationMode: { kind: 'string' },
                                    use: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userNameTemplate: {
                                kind: 'object',
                                properties: {
                                    pushStatus: { kind: 'string' },
                                    suffix: { kind: 'string' },
                                    template: { kind: 'string' },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    features: { kind: 'array', subType: { kind: 'string' } },
                    id: { kind: 'string' },
                    label: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    licensing: {
                        kind: 'object',
                        properties: { seatCount: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    settings: {
                        kind: 'object',
                        properties: {
                            app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            implicitAssignment: { kind: 'boolean' },
                            inlineHookId: { kind: 'string' },
                            notes: {
                                kind: 'object',
                                properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            notifications: {
                                kind: 'object',
                                properties: {
                                    vpn: {
                                        kind: 'object',
                                        properties: {
                                            helpUrl: { kind: 'string' },
                                            message: { kind: 'string' },
                                            network: {
                                                kind: 'object',
                                                properties: {
                                                    connection: { kind: 'string' },
                                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                                    include: { kind: 'array', subType: { kind: 'string' } },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    signOnMode: { kind: 'string' },
                    status: { kind: 'string' },
                    visibility: {
                        kind: 'object',
                        properties: {
                            appLinks: {
                                kind: 'object',
                                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            autoLaunch: { kind: 'boolean' },
                            autoSubmitToolbar: { kind: 'boolean' },
                            hide: {
                                kind: 'object',
                                properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Delete Application',
        name: 'Okta: Delete Application',
        description: 'Removes an inactive application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List client secrets',
        name: 'Okta: List client secrets',
        description: "Enumerates the client's collection of secrets",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        client_secret: { kind: 'string' },
                        created: { kind: 'string' },
                        id: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        secret_hash: { kind: 'string' },
                        status: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'credentials', 'secrets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Add new client secret',
        name: 'Okta: Add new client secret',
        description: "Adds a new secret to the client's collection of secrets.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            body: { kind: 'object', properties: { client_secret: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    client_secret: { kind: 'string' },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    secret_hash: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'credentials', 'secrets'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get client secret',
        name: 'Okta: Get client secret',
        description: 'Gets a specific client secret by secretId',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            secretId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    client_secret: { kind: 'string' },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    secret_hash: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'credentials', 'secrets', input.inPorts.secretId].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Activate a client secret',
        name: 'Okta: Activate a client secret',
        description: 'Activates a specific client secret by secretId',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            secretId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    client_secret: { kind: 'string' },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    secret_hash: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: [
                        '',
                        'api',
                        'v1',
                        'apps',
                        input.inPorts.appId,
                        'credentials',
                        'secrets',
                        input.inPorts.secretId,
                        'lifecycle',
                        'activate',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Deactivate a client secret',
        name: 'Okta: Deactivate a client secret',
        description: 'Deactivates a specific client secret by secretId',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            secretId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    client_secret: { kind: 'string' },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    secret_hash: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: [
                        '',
                        'api',
                        'v1',
                        'apps',
                        input.inPorts.appId,
                        'credentials',
                        'secrets',
                        input.inPorts.secretId,
                        'lifecycle',
                        'deactivate',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Fetches the Feature objects for an application.',
        name: 'Okta: Fetches the Feature objects for an application.',
        description: 'List Features for application',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        capabilities: {
                            kind: 'object',
                            properties: {
                                create: {
                                    kind: 'object',
                                    properties: {
                                        lifecycleCreate: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                update: {
                                    kind: 'object',
                                    properties: {
                                        lifecycleDeactivate: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        password: {
                                            kind: 'object',
                                            properties: {
                                                change: { kind: 'string' },
                                                seed: { kind: 'string' },
                                                status: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        profile: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        description: { kind: 'string' },
                        name: { kind: 'string' },
                        status: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'features'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Fetches a Feature object for an application.',
        name: 'Okta: Fetches a Feature object for an application.',
        description: 'Fetches a Feature object for an application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    capabilities: {
                        kind: 'object',
                        properties: {
                            create: {
                                kind: 'object',
                                properties: {
                                    lifecycleCreate: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            update: {
                                kind: 'object',
                                properties: {
                                    lifecycleDeactivate: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    password: {
                                        kind: 'object',
                                        properties: { change: { kind: 'string' }, seed: { kind: 'string' }, status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    profile: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    description: { kind: 'string' },
                    name: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'features', input.inPorts.name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Updates a Feature object for an application.',
        name: 'Okta: Updates a Feature object for an application.',
        description: 'Updates a Feature object for an application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    create: {
                        kind: 'object',
                        properties: {
                            lifecycleCreate: {
                                kind: 'object',
                                properties: { status: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    update: {
                        kind: 'object',
                        properties: {
                            lifecycleDeactivate: {
                                kind: 'object',
                                properties: { status: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            password: {
                                kind: 'object',
                                properties: { change: { kind: 'string' }, seed: { kind: 'string' }, status: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            profile: {
                                kind: 'object',
                                properties: { status: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    capabilities: {
                        kind: 'object',
                        properties: {
                            create: {
                                kind: 'object',
                                properties: {
                                    lifecycleCreate: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            update: {
                                kind: 'object',
                                properties: {
                                    lifecycleDeactivate: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    password: {
                                        kind: 'object',
                                        properties: { change: { kind: 'string' }, seed: { kind: 'string' }, status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    profile: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    description: { kind: 'string' },
                    name: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'features', input.inPorts.name].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Groups Assigned to Application',
        name: 'Okta: List Groups Assigned to Application',
        description: 'Enumerates group assignments for an application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            q: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['q', 'after', 'limit', 'expand'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        id: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        priority: { kind: 'number' },
                        profile: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'groups'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            after: input.inPorts.after,
                            limit: input.inPorts.limit,
                            expand: input.inPorts.expand,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get Assigned Group for Application',
        name: 'Okta: Get Assigned Group for Application',
        description: 'Fetches an application group assignment',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            groupId: { kind: 'string' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['expand'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    priority: { kind: 'number' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'groups', input.inPorts.groupId].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ expand: input.inPorts.expand }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Assign Group to Application',
        name: 'Okta: Assign Group to Application',
        description: 'Assigns a group to an application',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            groupId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    priority: { kind: 'number' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    priority: { kind: 'number' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'groups', input.inPorts.groupId].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Remove Group from Application',
        name: 'Okta: Remove Group from Application',
        description: 'Removes a group assignment from an application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            groupId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'groups', input.inPorts.groupId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Activate Application',
        name: 'Okta: Activate Application',
        description: 'Activates an inactive application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'lifecycle', 'activate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Deactivate Application',
        name: 'Okta: Deactivate Application',
        description: 'Deactivates an active application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'lifecycle', 'deactivate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Users Assigned to Application',
        name: 'Okta: List Users Assigned to Application',
        description: 'Enumerates all assigned [application users](#application-user-model) for an application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            q: { kind: 'string' },
            query_scope: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
            filter: { kind: 'string' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['q', 'query_scope', 'after', 'limit', 'filter', 'expand'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        credentials: {
                            kind: 'object',
                            properties: {
                                password: {
                                    kind: 'object',
                                    properties: { value: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                userName: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        externalId: { kind: 'string' },
                        id: { kind: 'string' },
                        lastSync: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        passwordChanged: { kind: 'string' },
                        profile: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        scope: { kind: 'string' },
                        status: { kind: 'string' },
                        statusChanged: { kind: 'string' },
                        syncState: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'users'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            query_scope: input.inPorts.query_scope,
                            after: input.inPorts.after,
                            limit: input.inPorts.limit,
                            filter: input.inPorts.filter,
                            expand: input.inPorts.expand,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Assign User to Application for SSO & Provisioning',
        name: 'Okta: Assign User to Application for SSO & Provisioning',
        description:
            'Assigns an user to an application with [credentials](#application-user-credentials-object) and an app-specific [profile](#application-user-profile-object). Profile mappings defined for the application are first applied before applying any profile properties specified in the request.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: { value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userName: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    externalId: { kind: 'string' },
                    id: { kind: 'string' },
                    lastSync: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    scope: { kind: 'string' },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    syncState: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: { value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userName: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    externalId: { kind: 'string' },
                    id: { kind: 'string' },
                    lastSync: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    scope: { kind: 'string' },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    syncState: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'users'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get Assigned User for Application',
        name: 'Okta: Get Assigned User for Application',
        description: 'Fetches a specific user assignment for application by `id`.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            userId: { kind: 'string' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['expand'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: { value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userName: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    externalId: { kind: 'string' },
                    id: { kind: 'string' },
                    lastSync: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    scope: { kind: 'string' },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    syncState: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'users', input.inPorts.userId].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ expand: input.inPorts.expand }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Update Application Profile for Assigned User',
        name: 'Okta: Update Application Profile for Assigned User',
        description: "Updates a user's profile for an application",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            userId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: { value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userName: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    externalId: { kind: 'string' },
                    id: { kind: 'string' },
                    lastSync: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    scope: { kind: 'string' },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    syncState: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: { value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            userName: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    externalId: { kind: 'string' },
                    id: { kind: 'string' },
                    lastSync: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    scope: { kind: 'string' },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    syncState: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'users', input.inPorts.userId].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Remove User from Application',
        name: 'Okta: Remove User from Application',
        description: 'Removes an assignment for a user from an application.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appId: { kind: 'string' },
            userId: { kind: 'string' },
            sendEmail: { kind: 'boolean' },
        },
        optionalInPorts: ['sendEmail'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'apps', input.inPorts.appId, 'users', input.inPorts.userId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({ sendEmail: input.inPorts.sendEmail }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Groups',
        name: 'Okta: List Groups',
        description:
            'Enumerates groups in your organization with pagination. A subset of groups can be returned that match a supported filter expression or query.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            q: { kind: 'string' },
            filter: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
            expand: { kind: 'string' },
            search: { kind: 'string' },
        },
        optionalInPorts: ['q', 'filter', 'after', 'limit', 'expand', 'search'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        id: { kind: 'string' },
                        lastMembershipUpdated: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        objectClass: { kind: 'array', subType: { kind: 'string' } },
                        profile: {
                            kind: 'object',
                            properties: { description: { kind: 'string' }, name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        type: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            filter: input.inPorts.filter,
                            after: input.inPorts.after,
                            limit: input.inPorts.limit,
                            expand: input.inPorts.expand,
                            search: input.inPorts.search,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Add Group',
        name: 'Okta: Add Group',
        description: 'Adds a new group with `OKTA_GROUP` type to your organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastMembershipUpdated: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    objectClass: { kind: 'array', subType: { kind: 'string' } },
                    profile: {
                        kind: 'object',
                        properties: { description: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastMembershipUpdated: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    objectClass: { kind: 'array', subType: { kind: 'string' } },
                    profile: {
                        kind: 'object',
                        properties: { description: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Group Rules',
        name: 'Okta: List Group Rules',
        description: 'Lists all group rules for your organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            limit: { kind: 'number' },
            after: { kind: 'string' },
            search: { kind: 'string' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['limit', 'after', 'search', 'expand'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        actions: {
                            kind: 'object',
                            properties: {
                                assignUserToGroups: {
                                    kind: 'object',
                                    properties: { groupIds: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        conditions: {
                            kind: 'object',
                            properties: {
                                expression: {
                                    kind: 'object',
                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                people: {
                                    kind: 'object',
                                    properties: {
                                        groups: {
                                            kind: 'object',
                                            properties: {
                                                exclude: { kind: 'array', subType: { kind: 'string' } },
                                                include: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        users: {
                                            kind: 'object',
                                            properties: {
                                                exclude: { kind: 'array', subType: { kind: 'string' } },
                                                include: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        id: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        name: { kind: 'string' },
                        status: { kind: 'string' },
                        type: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', 'rules'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            limit: input.inPorts.limit,
                            after: input.inPorts.after,
                            search: input.inPorts.search,
                            expand: input.inPorts.expand,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Create Group Rule',
        name: 'Okta: Create Group Rule',
        description: 'Creates a group rule to dynamically add users to the specified group if they match the condition',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    actions: {
                        kind: 'object',
                        properties: {
                            assignUserToGroups: {
                                kind: 'object',
                                properties: { groupIds: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'object',
                        properties: {
                            expression: {
                                kind: 'object',
                                properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            people: {
                                kind: 'object',
                                properties: {
                                    groups: {
                                        kind: 'object',
                                        properties: {
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    users: {
                                        kind: 'object',
                                        properties: {
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    name: { kind: 'string' },
                    status: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    actions: {
                        kind: 'object',
                        properties: {
                            assignUserToGroups: {
                                kind: 'object',
                                properties: { groupIds: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'object',
                        properties: {
                            expression: {
                                kind: 'object',
                                properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            people: {
                                kind: 'object',
                                properties: {
                                    groups: {
                                        kind: 'object',
                                        properties: {
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    users: {
                                        kind: 'object',
                                        properties: {
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    name: { kind: 'string' },
                    status: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', 'rules'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get Group Rule',
        name: 'Okta: Get Group Rule',
        description: 'Fetches a specific group rule by id from your organization',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            ruleId: { kind: 'string' },
            expand: { kind: 'string' },
        },
        optionalInPorts: ['expand'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    actions: {
                        kind: 'object',
                        properties: {
                            assignUserToGroups: {
                                kind: 'object',
                                properties: { groupIds: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'object',
                        properties: {
                            expression: {
                                kind: 'object',
                                properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            people: {
                                kind: 'object',
                                properties: {
                                    groups: {
                                        kind: 'object',
                                        properties: {
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    users: {
                                        kind: 'object',
                                        properties: {
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    name: { kind: 'string' },
                    status: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', 'rules', input.inPorts.ruleId].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ expand: input.inPorts.expand }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Delete a group Rule',
        name: 'Okta: Delete a group Rule',
        description: 'Removes a specific group rule by id from your organization',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            ruleId: { kind: 'string' },
            removeUsers: { kind: 'boolean' },
        },
        optionalInPorts: ['removeUsers'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', 'rules', input.inPorts.ruleId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(
                        Object.entries({ removeUsers: input.inPorts.removeUsers }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Activate a group Rule',
        name: 'Okta: Activate a group Rule',
        description: 'Activates a specific group rule by id from your organization',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            ruleId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', 'rules', input.inPorts.ruleId, 'lifecycle', 'activate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Deactivate a group Rule',
        name: 'Okta: Deactivate a group Rule',
        description: 'Deactivates a specific group rule by id from your organization',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            ruleId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', 'rules', input.inPorts.ruleId, 'lifecycle', 'deactivate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Update Group',
        name: 'Okta: Update Group',
        description: 'Updates the profile for a group with `OKTA_GROUP` type from your organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            groupId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastMembershipUpdated: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    objectClass: { kind: 'array', subType: { kind: 'string' } },
                    profile: {
                        kind: 'object',
                        properties: { description: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    id: { kind: 'string' },
                    lastMembershipUpdated: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    objectClass: { kind: 'array', subType: { kind: 'string' } },
                    profile: {
                        kind: 'object',
                        properties: { description: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', input.inPorts.groupId].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Remove Group',
        name: 'Okta: Remove Group',
        description: 'Removes a group with `OKTA_GROUP` type from your organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            groupId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', input.inPorts.groupId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Assigned Applications',
        name: 'Okta: List Assigned Applications',
        description: 'Enumerates all applications that are assigned to a group.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            groupId: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
        },
        optionalInPorts: ['after', 'limit'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        accessibility: {
                            kind: 'object',
                            properties: {
                                errorRedirectUrl: { kind: 'string' },
                                loginRedirectUrl: { kind: 'string' },
                                selfService: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        credentials: {
                            kind: 'object',
                            properties: {
                                signing: {
                                    kind: 'object',
                                    properties: {
                                        kid: { kind: 'string' },
                                        lastRotated: { kind: 'string' },
                                        nextRotation: { kind: 'string' },
                                        rotationMode: { kind: 'string' },
                                        use: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                userNameTemplate: {
                                    kind: 'object',
                                    properties: {
                                        pushStatus: { kind: 'string' },
                                        suffix: { kind: 'string' },
                                        template: { kind: 'string' },
                                        type: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        features: { kind: 'array', subType: { kind: 'string' } },
                        id: { kind: 'string' },
                        label: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        licensing: {
                            kind: 'object',
                            properties: { seatCount: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        name: { kind: 'string' },
                        profile: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        settings: {
                            kind: 'object',
                            properties: {
                                app: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                implicitAssignment: { kind: 'boolean' },
                                inlineHookId: { kind: 'string' },
                                notes: {
                                    kind: 'object',
                                    properties: { admin: { kind: 'string' }, enduser: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                notifications: {
                                    kind: 'object',
                                    properties: {
                                        vpn: {
                                            kind: 'object',
                                            properties: {
                                                helpUrl: { kind: 'string' },
                                                message: { kind: 'string' },
                                                network: {
                                                    kind: 'object',
                                                    properties: {
                                                        connection: { kind: 'string' },
                                                        exclude: { kind: 'array', subType: { kind: 'string' } },
                                                        include: { kind: 'array', subType: { kind: 'string' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        signOnMode: { kind: 'string' },
                        status: { kind: 'string' },
                        visibility: {
                            kind: 'object',
                            properties: {
                                appLinks: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                autoLaunch: { kind: 'boolean' },
                                autoSubmitToolbar: { kind: 'boolean' },
                                hide: {
                                    kind: 'object',
                                    properties: { iOS: { kind: 'boolean' }, web: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', input.inPorts.groupId, 'apps'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ after: input.inPorts.after, limit: input.inPorts.limit }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Group Members',
        name: 'Okta: List Group Members',
        description: 'Enumerates all users that are a member of a group.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            groupId: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
        },
        optionalInPorts: ['after', 'limit'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        activated: { kind: 'string' },
                        created: { kind: 'string' },
                        credentials: {
                            kind: 'object',
                            properties: {
                                password: {
                                    kind: 'object',
                                    properties: {
                                        hash: {
                                            kind: 'object',
                                            properties: {
                                                algorithm: { kind: 'string' },
                                                salt: { kind: 'string' },
                                                saltOrder: { kind: 'string' },
                                                value: { kind: 'string' },
                                                workFactor: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        hook: {
                                            kind: 'object',
                                            properties: { type: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        value: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                provider: {
                                    kind: 'object',
                                    properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                recovery_question: {
                                    kind: 'object',
                                    properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        id: { kind: 'string' },
                        lastLogin: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        passwordChanged: { kind: 'string' },
                        profile: {
                            kind: 'object',
                            properties: {
                                city: { kind: 'string' },
                                costCenter: { kind: 'string' },
                                countryCode: { kind: 'string' },
                                department: { kind: 'string' },
                                displayName: { kind: 'string' },
                                division: { kind: 'string' },
                                email: { kind: 'string' },
                                employeeNumber: { kind: 'string' },
                                firstName: { kind: 'string' },
                                honorificPrefix: { kind: 'string' },
                                honorificSuffix: { kind: 'string' },
                                lastName: { kind: 'string' },
                                locale: { kind: 'string' },
                                login: { kind: 'string' },
                                manager: { kind: 'string' },
                                managerId: { kind: 'string' },
                                middleName: { kind: 'string' },
                                mobilePhone: { kind: 'string' },
                                nickName: { kind: 'string' },
                                organization: { kind: 'string' },
                                postalAddress: { kind: 'string' },
                                preferredLanguage: { kind: 'string' },
                                primaryPhone: { kind: 'string' },
                                profileUrl: { kind: 'string' },
                                secondEmail: { kind: 'string' },
                                state: { kind: 'string' },
                                streetAddress: { kind: 'string' },
                                timezone: { kind: 'string' },
                                title: { kind: 'string' },
                                userType: { kind: 'string' },
                                zipCode: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        status: { kind: 'string' },
                        statusChanged: { kind: 'string' },
                        transitioningToStatus: { kind: 'string' },
                        type: {
                            kind: 'object',
                            properties: {
                                _links: {
                                    kind: 'object',
                                    properties: {
                                        type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                created: { kind: 'string' },
                                createdBy: { kind: 'string' },
                                default: { kind: 'boolean' },
                                description: { kind: 'string' },
                                displayName: { kind: 'string' },
                                id: { kind: 'string' },
                                lastUpdated: { kind: 'string' },
                                lastUpdatedBy: { kind: 'string' },
                                name: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', input.inPorts.groupId, 'users'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ after: input.inPorts.after, limit: input.inPorts.limit }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Add User to Group',
        name: 'Okta: Add User to Group',
        description: "Adds a user to a group with 'OKTA_GROUP' type.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            groupId: { kind: 'string' },
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', input.inPorts.groupId, 'users', input.inPorts.userId].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Remove User from Group',
        name: 'Okta: Remove User from Group',
        description: "Removes a user from a group with 'OKTA_GROUP' type.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            groupId: { kind: 'string' },
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'groups', input.inPorts.groupId, 'users', input.inPorts.userId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Find Users',
        name: 'Okta: Find Users',
        description: 'Find all the users linked to an identity provider',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            idpId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        externalId: { kind: 'string' },
                        id: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        profile: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'idps', input.inPorts.idpId, 'users'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Fetches the Schema for an App User',
        name: 'Okta: Fetches the Schema for an App User',
        description: 'Fetches the Schema for an App User',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            appInstanceId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    $schema: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    definitions: {
                        kind: 'object',
                        properties: {
                            base: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'string' },
                                    properties: {
                                        kind: 'object',
                                        properties: {
                                            city: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            costCenter: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            countryCode: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            department: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            displayName: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            division: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            email: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            employeeNumber: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            firstName: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            honorificPrefix: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            honorificSuffix: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            lastName: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            locale: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            login: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            manager: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            managerId: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            middleName: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            mobilePhone: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            nickName: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            organization: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            postalAddress: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            preferredLanguage: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            primaryPhone: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            profileUrl: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secondEmail: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            state: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            streetAddress: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            timezone: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            title: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            userType: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            zipCode: {
                                                kind: 'object',
                                                properties: {
                                                    description: { kind: 'string' },
                                                    enum: { kind: 'array', subType: { kind: 'string' } },
                                                    externalName: { kind: 'string' },
                                                    externalNamespace: { kind: 'string' },
                                                    items: {
                                                        kind: 'object',
                                                        properties: {
                                                            enum: { kind: 'array', subType: { kind: 'string' } },
                                                            oneOf: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    master: {
                                                        kind: 'object',
                                                        properties: {
                                                            priority: {
                                                                kind: 'array',
                                                                subType: {
                                                                    kind: 'object',
                                                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                            },
                                                            type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    maxLength: { kind: 'number' },
                                                    minLength: { kind: 'number' },
                                                    mutability: { kind: 'string' },
                                                    oneOf: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { const: { kind: 'string' }, title: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    pattern: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: { action: { kind: 'string' }, principal: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    required: { kind: 'boolean' },
                                                    scope: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    title: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    union: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                    unique: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    required: { kind: 'array', subType: { kind: 'string' } },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            custom: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'string' },
                                    properties: {
                                        kind: 'object',
                                        properties: {
                                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            'x-okta-tags': { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    required: { kind: 'array', subType: { kind: 'string' } },
                                    type: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    name: { kind: 'string' },
                    properties: {
                        kind: 'object',
                        properties: {
                            profile: {
                                kind: 'object',
                                properties: {
                                    allOf: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { $ref: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    title: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'meta', 'schemas', 'apps', input.inPorts.appInstanceId, 'default'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List Users',
        name: 'Okta: List Users',
        description:
            "Lists users that do not have a status of 'DEPROVISIONED' (by default), up to the maximum (200 for most orgs), with pagination in most cases. A subset of users can be returned that match a supported filter expression or search criteria.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            q: { kind: 'string' },
            after: { kind: 'string' },
            limit: { kind: 'number' },
            filter: { kind: 'string' },
            search: { kind: 'string' },
            sortBy: { kind: 'string' },
            sortOrder: { kind: 'string' },
        },
        optionalInPorts: ['q', 'after', 'limit', 'filter', 'search', 'sortBy', 'sortOrder'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        activated: { kind: 'string' },
                        created: { kind: 'string' },
                        credentials: {
                            kind: 'object',
                            properties: {
                                password: {
                                    kind: 'object',
                                    properties: {
                                        hash: {
                                            kind: 'object',
                                            properties: {
                                                algorithm: { kind: 'string' },
                                                salt: { kind: 'string' },
                                                saltOrder: { kind: 'string' },
                                                value: { kind: 'string' },
                                                workFactor: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        hook: {
                                            kind: 'object',
                                            properties: { type: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        value: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                provider: {
                                    kind: 'object',
                                    properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                recovery_question: {
                                    kind: 'object',
                                    properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        id: { kind: 'string' },
                        lastLogin: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        passwordChanged: { kind: 'string' },
                        profile: {
                            kind: 'object',
                            properties: {
                                city: { kind: 'string' },
                                costCenter: { kind: 'string' },
                                countryCode: { kind: 'string' },
                                department: { kind: 'string' },
                                displayName: { kind: 'string' },
                                division: { kind: 'string' },
                                email: { kind: 'string' },
                                employeeNumber: { kind: 'string' },
                                firstName: { kind: 'string' },
                                honorificPrefix: { kind: 'string' },
                                honorificSuffix: { kind: 'string' },
                                lastName: { kind: 'string' },
                                locale: { kind: 'string' },
                                login: { kind: 'string' },
                                manager: { kind: 'string' },
                                managerId: { kind: 'string' },
                                middleName: { kind: 'string' },
                                mobilePhone: { kind: 'string' },
                                nickName: { kind: 'string' },
                                organization: { kind: 'string' },
                                postalAddress: { kind: 'string' },
                                preferredLanguage: { kind: 'string' },
                                primaryPhone: { kind: 'string' },
                                profileUrl: { kind: 'string' },
                                secondEmail: { kind: 'string' },
                                state: { kind: 'string' },
                                streetAddress: { kind: 'string' },
                                timezone: { kind: 'string' },
                                title: { kind: 'string' },
                                userType: { kind: 'string' },
                                zipCode: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        status: { kind: 'string' },
                        statusChanged: { kind: 'string' },
                        transitioningToStatus: { kind: 'string' },
                        type: {
                            kind: 'object',
                            properties: {
                                _links: {
                                    kind: 'object',
                                    properties: {
                                        type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                created: { kind: 'string' },
                                createdBy: { kind: 'string' },
                                default: { kind: 'boolean' },
                                description: { kind: 'string' },
                                displayName: { kind: 'string' },
                                id: { kind: 'string' },
                                lastUpdated: { kind: 'string' },
                                lastUpdatedBy: { kind: 'string' },
                                name: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            after: input.inPorts.after,
                            limit: input.inPorts.limit,
                            filter: input.inPorts.filter,
                            search: input.inPorts.search,
                            sortBy: input.inPorts.sortBy,
                            sortOrder: input.inPorts.sortOrder,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Create User',
        name: 'Okta: Create User',
        description: 'Creates a new user in your Okta organization with or without credentials.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            activate: { kind: 'boolean' },
            provider: { kind: 'boolean' },
            nextLogin: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: {
                                    hash: {
                                        kind: 'object',
                                        properties: {
                                            algorithm: { kind: 'string' },
                                            salt: { kind: 'string' },
                                            saltOrder: { kind: 'string' },
                                            value: { kind: 'string' },
                                            workFactor: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    hook: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    value: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            provider: {
                                kind: 'object',
                                properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            recovery_question: {
                                kind: 'object',
                                properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    groupIds: { kind: 'array', subType: { kind: 'string' } },
                    profile: {
                        kind: 'object',
                        properties: {
                            city: { kind: 'string' },
                            costCenter: { kind: 'string' },
                            countryCode: { kind: 'string' },
                            department: { kind: 'string' },
                            displayName: { kind: 'string' },
                            division: { kind: 'string' },
                            email: { kind: 'string' },
                            employeeNumber: { kind: 'string' },
                            firstName: { kind: 'string' },
                            honorificPrefix: { kind: 'string' },
                            honorificSuffix: { kind: 'string' },
                            lastName: { kind: 'string' },
                            locale: { kind: 'string' },
                            login: { kind: 'string' },
                            manager: { kind: 'string' },
                            managerId: { kind: 'string' },
                            middleName: { kind: 'string' },
                            mobilePhone: { kind: 'string' },
                            nickName: { kind: 'string' },
                            organization: { kind: 'string' },
                            postalAddress: { kind: 'string' },
                            preferredLanguage: { kind: 'string' },
                            primaryPhone: { kind: 'string' },
                            profileUrl: { kind: 'string' },
                            secondEmail: { kind: 'string' },
                            state: { kind: 'string' },
                            streetAddress: { kind: 'string' },
                            timezone: { kind: 'string' },
                            title: { kind: 'string' },
                            userType: { kind: 'string' },
                            zipCode: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    type: {
                        kind: 'object',
                        properties: {
                            _links: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            created: { kind: 'string' },
                            createdBy: { kind: 'string' },
                            default: { kind: 'boolean' },
                            description: { kind: 'string' },
                            displayName: { kind: 'string' },
                            id: { kind: 'string' },
                            lastUpdated: { kind: 'string' },
                            lastUpdatedBy: { kind: 'string' },
                            name: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['activate', 'provider', 'nextLogin', 'body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    activated: { kind: 'string' },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: {
                                    hash: {
                                        kind: 'object',
                                        properties: {
                                            algorithm: { kind: 'string' },
                                            salt: { kind: 'string' },
                                            saltOrder: { kind: 'string' },
                                            value: { kind: 'string' },
                                            workFactor: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    hook: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    value: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            provider: {
                                kind: 'object',
                                properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            recovery_question: {
                                kind: 'object',
                                properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastLogin: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            city: { kind: 'string' },
                            costCenter: { kind: 'string' },
                            countryCode: { kind: 'string' },
                            department: { kind: 'string' },
                            displayName: { kind: 'string' },
                            division: { kind: 'string' },
                            email: { kind: 'string' },
                            employeeNumber: { kind: 'string' },
                            firstName: { kind: 'string' },
                            honorificPrefix: { kind: 'string' },
                            honorificSuffix: { kind: 'string' },
                            lastName: { kind: 'string' },
                            locale: { kind: 'string' },
                            login: { kind: 'string' },
                            manager: { kind: 'string' },
                            managerId: { kind: 'string' },
                            middleName: { kind: 'string' },
                            mobilePhone: { kind: 'string' },
                            nickName: { kind: 'string' },
                            organization: { kind: 'string' },
                            postalAddress: { kind: 'string' },
                            preferredLanguage: { kind: 'string' },
                            primaryPhone: { kind: 'string' },
                            profileUrl: { kind: 'string' },
                            secondEmail: { kind: 'string' },
                            state: { kind: 'string' },
                            streetAddress: { kind: 'string' },
                            timezone: { kind: 'string' },
                            title: { kind: 'string' },
                            userType: { kind: 'string' },
                            zipCode: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    transitioningToStatus: { kind: 'string' },
                    type: {
                        kind: 'object',
                        properties: {
                            _links: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            created: { kind: 'string' },
                            createdBy: { kind: 'string' },
                            default: { kind: 'boolean' },
                            description: { kind: 'string' },
                            displayName: { kind: 'string' },
                            id: { kind: 'string' },
                            lastUpdated: { kind: 'string' },
                            lastUpdatedBy: { kind: 'string' },
                            name: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(
                        Object.entries({
                            activate: input.inPorts.activate,
                            provider: input.inPorts.provider,
                            nextLogin: input.inPorts.nextLogin,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get User',
        name: 'Okta: Get User',
        description: 'Fetches a user from your Okta organization.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    activated: { kind: 'string' },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: {
                                    hash: {
                                        kind: 'object',
                                        properties: {
                                            algorithm: { kind: 'string' },
                                            salt: { kind: 'string' },
                                            saltOrder: { kind: 'string' },
                                            value: { kind: 'string' },
                                            workFactor: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    hook: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    value: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            provider: {
                                kind: 'object',
                                properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            recovery_question: {
                                kind: 'object',
                                properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastLogin: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            city: { kind: 'string' },
                            costCenter: { kind: 'string' },
                            countryCode: { kind: 'string' },
                            department: { kind: 'string' },
                            displayName: { kind: 'string' },
                            division: { kind: 'string' },
                            email: { kind: 'string' },
                            employeeNumber: { kind: 'string' },
                            firstName: { kind: 'string' },
                            honorificPrefix: { kind: 'string' },
                            honorificSuffix: { kind: 'string' },
                            lastName: { kind: 'string' },
                            locale: { kind: 'string' },
                            login: { kind: 'string' },
                            manager: { kind: 'string' },
                            managerId: { kind: 'string' },
                            middleName: { kind: 'string' },
                            mobilePhone: { kind: 'string' },
                            nickName: { kind: 'string' },
                            organization: { kind: 'string' },
                            postalAddress: { kind: 'string' },
                            preferredLanguage: { kind: 'string' },
                            primaryPhone: { kind: 'string' },
                            profileUrl: { kind: 'string' },
                            secondEmail: { kind: 'string' },
                            state: { kind: 'string' },
                            streetAddress: { kind: 'string' },
                            timezone: { kind: 'string' },
                            title: { kind: 'string' },
                            userType: { kind: 'string' },
                            zipCode: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    transitioningToStatus: { kind: 'string' },
                    type: {
                        kind: 'object',
                        properties: {
                            _links: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            created: { kind: 'string' },
                            createdBy: { kind: 'string' },
                            default: { kind: 'boolean' },
                            description: { kind: 'string' },
                            displayName: { kind: 'string' },
                            id: { kind: 'string' },
                            lastUpdated: { kind: 'string' },
                            lastUpdatedBy: { kind: 'string' },
                            name: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Update User',
        name: 'Okta: Update User',
        description: "Update a user's profile and/or credentials using strict-update semantics.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            strict: { kind: 'boolean' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    activated: { kind: 'string' },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: {
                                    hash: {
                                        kind: 'object',
                                        properties: {
                                            algorithm: { kind: 'string' },
                                            salt: { kind: 'string' },
                                            saltOrder: { kind: 'string' },
                                            value: { kind: 'string' },
                                            workFactor: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    hook: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    value: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            provider: {
                                kind: 'object',
                                properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            recovery_question: {
                                kind: 'object',
                                properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastLogin: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            city: { kind: 'string' },
                            costCenter: { kind: 'string' },
                            countryCode: { kind: 'string' },
                            department: { kind: 'string' },
                            displayName: { kind: 'string' },
                            division: { kind: 'string' },
                            email: { kind: 'string' },
                            employeeNumber: { kind: 'string' },
                            firstName: { kind: 'string' },
                            honorificPrefix: { kind: 'string' },
                            honorificSuffix: { kind: 'string' },
                            lastName: { kind: 'string' },
                            locale: { kind: 'string' },
                            login: { kind: 'string' },
                            manager: { kind: 'string' },
                            managerId: { kind: 'string' },
                            middleName: { kind: 'string' },
                            mobilePhone: { kind: 'string' },
                            nickName: { kind: 'string' },
                            organization: { kind: 'string' },
                            postalAddress: { kind: 'string' },
                            preferredLanguage: { kind: 'string' },
                            primaryPhone: { kind: 'string' },
                            profileUrl: { kind: 'string' },
                            secondEmail: { kind: 'string' },
                            state: { kind: 'string' },
                            streetAddress: { kind: 'string' },
                            timezone: { kind: 'string' },
                            title: { kind: 'string' },
                            userType: { kind: 'string' },
                            zipCode: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    transitioningToStatus: { kind: 'string' },
                    type: {
                        kind: 'object',
                        properties: {
                            _links: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            created: { kind: 'string' },
                            createdBy: { kind: 'string' },
                            default: { kind: 'boolean' },
                            description: { kind: 'string' },
                            displayName: { kind: 'string' },
                            id: { kind: 'string' },
                            lastUpdated: { kind: 'string' },
                            lastUpdatedBy: { kind: 'string' },
                            name: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['strict', 'body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    activated: { kind: 'string' },
                    created: { kind: 'string' },
                    credentials: {
                        kind: 'object',
                        properties: {
                            password: {
                                kind: 'object',
                                properties: {
                                    hash: {
                                        kind: 'object',
                                        properties: {
                                            algorithm: { kind: 'string' },
                                            salt: { kind: 'string' },
                                            saltOrder: { kind: 'string' },
                                            value: { kind: 'string' },
                                            workFactor: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    hook: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    value: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            provider: {
                                kind: 'object',
                                properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            recovery_question: {
                                kind: 'object',
                                properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    id: { kind: 'string' },
                    lastLogin: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    passwordChanged: { kind: 'string' },
                    profile: {
                        kind: 'object',
                        properties: {
                            city: { kind: 'string' },
                            costCenter: { kind: 'string' },
                            countryCode: { kind: 'string' },
                            department: { kind: 'string' },
                            displayName: { kind: 'string' },
                            division: { kind: 'string' },
                            email: { kind: 'string' },
                            employeeNumber: { kind: 'string' },
                            firstName: { kind: 'string' },
                            honorificPrefix: { kind: 'string' },
                            honorificSuffix: { kind: 'string' },
                            lastName: { kind: 'string' },
                            locale: { kind: 'string' },
                            login: { kind: 'string' },
                            manager: { kind: 'string' },
                            managerId: { kind: 'string' },
                            middleName: { kind: 'string' },
                            mobilePhone: { kind: 'string' },
                            nickName: { kind: 'string' },
                            organization: { kind: 'string' },
                            postalAddress: { kind: 'string' },
                            preferredLanguage: { kind: 'string' },
                            primaryPhone: { kind: 'string' },
                            profileUrl: { kind: 'string' },
                            secondEmail: { kind: 'string' },
                            state: { kind: 'string' },
                            streetAddress: { kind: 'string' },
                            timezone: { kind: 'string' },
                            title: { kind: 'string' },
                            userType: { kind: 'string' },
                            zipCode: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    status: { kind: 'string' },
                    statusChanged: { kind: 'string' },
                    transitioningToStatus: { kind: 'string' },
                    type: {
                        kind: 'object',
                        properties: {
                            _links: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            created: { kind: 'string' },
                            createdBy: { kind: 'string' },
                            default: { kind: 'boolean' },
                            description: { kind: 'string' },
                            displayName: { kind: 'string' },
                            id: { kind: 'string' },
                            lastUpdated: { kind: 'string' },
                            lastUpdatedBy: { kind: 'string' },
                            name: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({ strict: input.inPorts.strict }).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Delete User',
        name: 'Okta: Delete User',
        description:
            'Deletes a user permanently.  This operation can only be performed on users that have a `DEPROVISIONED` status.  **This action cannot be recovered!**',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            sendEmail: { kind: 'boolean' },
        },
        optionalInPorts: ['sendEmail'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({ sendEmail: input.inPorts.sendEmail }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get Assigned App Links',
        name: 'Okta: Get Assigned App Links',
        description: 'Fetches appLinks for all direct or indirect (via group membership) assigned applications.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        appAssignmentId: { kind: 'string' },
                        appInstanceId: { kind: 'string' },
                        appName: { kind: 'string' },
                        credentialsSetup: { kind: 'boolean' },
                        hidden: { kind: 'boolean' },
                        id: { kind: 'string' },
                        label: { kind: 'string' },
                        linkUrl: { kind: 'string' },
                        logoUrl: { kind: 'string' },
                        sortOrder: { kind: 'number' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'appLinks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Change Password',
        name: 'Okta: Change Password',
        description:
            "Changes a user's password by validating the user's current password. This operation can only be performed on users in `STAGED`, `ACTIVE`, `PASSWORD_EXPIRED`, or `RECOVERY` status that have a valid password credential",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            strict: { kind: 'boolean' },
            body: {
                kind: 'object',
                properties: {
                    newPassword: {
                        kind: 'object',
                        properties: {
                            hash: {
                                kind: 'object',
                                properties: {
                                    algorithm: { kind: 'string' },
                                    salt: { kind: 'string' },
                                    saltOrder: { kind: 'string' },
                                    value: { kind: 'string' },
                                    workFactor: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            hook: { kind: 'object', properties: { type: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            value: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    oldPassword: {
                        kind: 'object',
                        properties: {
                            hash: {
                                kind: 'object',
                                properties: {
                                    algorithm: { kind: 'string' },
                                    salt: { kind: 'string' },
                                    saltOrder: { kind: 'string' },
                                    value: { kind: 'string' },
                                    workFactor: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            hook: { kind: 'object', properties: { type: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            value: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['strict', 'body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    password: {
                        kind: 'object',
                        properties: {
                            hash: {
                                kind: 'object',
                                properties: {
                                    algorithm: { kind: 'string' },
                                    salt: { kind: 'string' },
                                    saltOrder: { kind: 'string' },
                                    value: { kind: 'string' },
                                    workFactor: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            hook: { kind: 'object', properties: { type: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            value: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    provider: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    recovery_question: {
                        kind: 'object',
                        properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'credentials', 'change_password'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ strict: input.inPorts.strict }).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Change Recovery Question',
        name: 'Okta: Change Recovery Question',
        description:
            "Changes a user's recovery question & answer credential by validating the user's current password.  This operation can only be performed on users in **STAGED**, **ACTIVE** or **RECOVERY** `status` that have a valid password credential",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    password: {
                        kind: 'object',
                        properties: {
                            hash: {
                                kind: 'object',
                                properties: {
                                    algorithm: { kind: 'string' },
                                    salt: { kind: 'string' },
                                    saltOrder: { kind: 'string' },
                                    value: { kind: 'string' },
                                    workFactor: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            hook: { kind: 'object', properties: { type: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            value: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    provider: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    recovery_question: {
                        kind: 'object',
                        properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    password: {
                        kind: 'object',
                        properties: {
                            hash: {
                                kind: 'object',
                                properties: {
                                    algorithm: { kind: 'string' },
                                    salt: { kind: 'string' },
                                    saltOrder: { kind: 'string' },
                                    value: { kind: 'string' },
                                    workFactor: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            hook: { kind: 'object', properties: { type: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            value: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    provider: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, type: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    recovery_question: {
                        kind: 'object',
                        properties: { answer: { kind: 'string' }, question: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'credentials', 'change_recovery_question'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Forgot Password',
        name: 'Okta: Forgot Password',
        description: '',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { resetPasswordUrl: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'credentials', 'forgot_password'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Enroll Factor',
        name: 'Okta: Enroll Factor',
        description: 'Enrolls a user with a supported factor.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            updatePhone: { kind: 'boolean' },
            templateId: { kind: 'string' },
            tokenLifetimeSeconds: { kind: 'number' },
            activate: { kind: 'boolean' },
            body: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    factorType: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    provider: { kind: 'string' },
                    status: { kind: 'string' },
                    verify: {
                        kind: 'object',
                        properties: {
                            activationToken: { kind: 'string' },
                            answer: { kind: 'string' },
                            attestation: { kind: 'string' },
                            clientData: { kind: 'string' },
                            nextPassCode: { kind: 'string' },
                            passCode: { kind: 'string' },
                            registrationData: { kind: 'string' },
                            stateToken: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['updatePhone', 'templateId', 'tokenLifetimeSeconds', 'activate', 'body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    factorType: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    provider: { kind: 'string' },
                    status: { kind: 'string' },
                    verify: {
                        kind: 'object',
                        properties: {
                            activationToken: { kind: 'string' },
                            answer: { kind: 'string' },
                            attestation: { kind: 'string' },
                            clientData: { kind: 'string' },
                            nextPassCode: { kind: 'string' },
                            passCode: { kind: 'string' },
                            registrationData: { kind: 'string' },
                            stateToken: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'factors'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(
                        Object.entries({
                            updatePhone: input.inPorts.updatePhone,
                            templateId: input.inPorts.templateId,
                            tokenLifetimeSeconds: input.inPorts.tokenLifetimeSeconds,
                            activate: input.inPorts.activate,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Activate Factor',
        name: 'Okta: Activate Factor',
        description: 'The `sms` and `token:software:totp` factor types require activation to complete the enrollment process.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            factorId: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    attestation: { kind: 'string' },
                    clientData: { kind: 'string' },
                    passCode: { kind: 'string' },
                    registrationData: { kind: 'string' },
                    stateToken: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    created: { kind: 'string' },
                    factorType: { kind: 'string' },
                    id: { kind: 'string' },
                    lastUpdated: { kind: 'string' },
                    provider: { kind: 'string' },
                    status: { kind: 'string' },
                    verify: {
                        kind: 'object',
                        properties: {
                            activationToken: { kind: 'string' },
                            answer: { kind: 'string' },
                            attestation: { kind: 'string' },
                            clientData: { kind: 'string' },
                            nextPassCode: { kind: 'string' },
                            passCode: { kind: 'string' },
                            registrationData: { kind: 'string' },
                            stateToken: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'factors', input.inPorts.factorId, 'lifecycle', 'activate'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Verify MFA Factor',
        name: 'Okta: Verify MFA Factor',
        description: 'Verifies an OTP for a `token` or `token:hardware` factor',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            factorId: { kind: 'string' },
            templateId: { kind: 'string' },
            tokenLifetimeSeconds: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    activationToken: { kind: 'string' },
                    answer: { kind: 'string' },
                    attestation: { kind: 'string' },
                    clientData: { kind: 'string' },
                    nextPassCode: { kind: 'string' },
                    passCode: { kind: 'string' },
                    registrationData: { kind: 'string' },
                    stateToken: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['templateId', 'tokenLifetimeSeconds', 'body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _embedded: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    expiresAt: { kind: 'string' },
                    factorResult: { kind: 'string' },
                    factorResultMessage: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'factors', input.inPorts.factorId, 'verify'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(
                        Object.entries({
                            templateId: input.inPorts.templateId,
                            tokenLifetimeSeconds: input.inPorts.tokenLifetimeSeconds,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get Member Groups',
        name: 'Okta: Get Member Groups',
        description: 'Fetches the groups of which the user is a member.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _embedded: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        id: { kind: 'string' },
                        lastMembershipUpdated: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        objectClass: { kind: 'array', subType: { kind: 'string' } },
                        profile: {
                            kind: 'object',
                            properties: { description: { kind: 'string' }, name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        type: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'groups'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Listing IdPs associated with a user',
        name: 'Okta: Listing IdPs associated with a user',
        description: 'Lists the IdPs associated with the user.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created: { kind: 'string' },
                        id: { kind: 'string' },
                        issuerMode: { kind: 'string' },
                        lastUpdated: { kind: 'string' },
                        name: { kind: 'string' },
                        policy: {
                            kind: 'object',
                            properties: {
                                accountLink: {
                                    kind: 'object',
                                    properties: {
                                        action: { kind: 'string' },
                                        filter: {
                                            kind: 'object',
                                            properties: {
                                                groups: {
                                                    kind: 'object',
                                                    properties: { include: { kind: 'array', subType: { kind: 'string' } } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                maxClockSkew: { kind: 'number' },
                                provisioning: {
                                    kind: 'object',
                                    properties: {
                                        action: { kind: 'string' },
                                        conditions: {
                                            kind: 'object',
                                            properties: {
                                                deprovisioned: {
                                                    kind: 'object',
                                                    properties: { action: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                suspended: {
                                                    kind: 'object',
                                                    properties: { action: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        groups: {
                                            kind: 'object',
                                            properties: {
                                                action: { kind: 'string' },
                                                assignments: { kind: 'array', subType: { kind: 'string' } },
                                                filter: { kind: 'array', subType: { kind: 'string' } },
                                                sourceAttributeName: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        profileMaster: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                subject: {
                                    kind: 'object',
                                    properties: {
                                        filter: { kind: 'string' },
                                        format: { kind: 'array', subType: { kind: 'string' } },
                                        matchAttribute: { kind: 'string' },
                                        matchType: { kind: 'string' },
                                        userNameTemplate: {
                                            kind: 'object',
                                            properties: { template: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        protocol: {
                            kind: 'object',
                            properties: {
                                algorithms: {
                                    kind: 'object',
                                    properties: {
                                        request: {
                                            kind: 'object',
                                            properties: {
                                                signature: {
                                                    kind: 'object',
                                                    properties: { algorithm: { kind: 'string' }, scope: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        response: {
                                            kind: 'object',
                                            properties: {
                                                signature: {
                                                    kind: 'object',
                                                    properties: { algorithm: { kind: 'string' }, scope: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                credentials: {
                                    kind: 'object',
                                    properties: {
                                        client: {
                                            kind: 'object',
                                            properties: { client_id: { kind: 'string' }, client_secret: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        signing: {
                                            kind: 'object',
                                            properties: {
                                                kid: { kind: 'string' },
                                                privateKey: { kind: 'string' },
                                                teamId: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        trust: {
                                            kind: 'object',
                                            properties: {
                                                audience: { kind: 'string' },
                                                issuer: { kind: 'string' },
                                                kid: { kind: 'string' },
                                                revocation: { kind: 'string' },
                                                revocationCacheLifetime: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                endpoints: {
                                    kind: 'object',
                                    properties: {
                                        acs: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        authorization: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        jwks: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        metadata: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        slo: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        sso: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        token: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        userInfo: {
                                            kind: 'object',
                                            properties: {
                                                binding: { kind: 'string' },
                                                destination: { kind: 'string' },
                                                type: { kind: 'string' },
                                                url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                issuer: {
                                    kind: 'object',
                                    properties: {
                                        binding: { kind: 'string' },
                                        destination: { kind: 'string' },
                                        type: { kind: 'string' },
                                        url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                relayState: {
                                    kind: 'object',
                                    properties: { format: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                scopes: { kind: 'array', subType: { kind: 'string' } },
                                settings: {
                                    kind: 'object',
                                    properties: { nameFormat: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                type: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        status: { kind: 'string' },
                        type: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'idps'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Activate User',
        name: 'Okta: Activate User',
        description:
            "Activates a user.  This operation can only be performed on users with a `STAGED` status.  Activation of a user is an asynchronous operation. The user will have the `transitioningToStatus` property with a value of `ACTIVE` during activation to indicate that the user hasn't completed the asynchronous operation.  The user will have a status of `ACTIVE` when the activation process is complete.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            sendEmail: { kind: 'boolean' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { activationToken: { kind: 'string' }, activationUrl: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'activate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ sendEmail: input.inPorts.sendEmail }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Deactivate User',
        name: 'Okta: Deactivate User',
        description:
            "Deactivates a user. This operation can only be performed on users that do not have a `DEPROVISIONED` status. While the asynchronous operation (triggered by HTTP header `Prefer: respond-async`) is proceeding the user's `transitioningToStatus` property is `DEPROVISIONED`. The user's status is `DEPROVISIONED` when the deactivation process is complete.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            sendEmail: { kind: 'boolean' },
        },
        optionalInPorts: ['sendEmail'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'deactivate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ sendEmail: input.inPorts.sendEmail }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Reactivate User',
        name: 'Okta: Reactivate User',
        description:
            'Reactivates a user.  This operation can only be performed on users with a `PROVISIONED` status.  This operation restarts the activation workflow if for some reason the user activation was not completed when using the activationToken from [Activate User](#activate-user).',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            sendEmail: { kind: 'boolean' },
        },
        optionalInPorts: ['sendEmail'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { activationToken: { kind: 'string' }, activationUrl: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'reactivate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ sendEmail: input.inPorts.sendEmail }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Reset Factors',
        name: 'Okta: Reset Factors',
        description:
            "This operation resets all factors for the specified user. All MFA factor enrollments returned to the unenrolled state. The user's status remains ACTIVE. This link is present only if the user is currently enrolled in one or more MFA factors.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'reset_factors'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Reset Password',
        name: 'Okta: Reset Password',
        description:
            "Generates a one-time token (OTT) that can be used to reset a user's password.  The OTT link can be automatically emailed to the user or returned to the API caller and distributed using a custom flow.",
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            sendEmail: { kind: 'boolean' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { resetPasswordUrl: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'reset_password'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ sendEmail: input.inPorts.sendEmail }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Suspend User',
        name: 'Okta: Suspend User',
        description:
            'Suspends a user.  This operation can only be performed on users with an `ACTIVE` status.  The user will have a status of `SUSPENDED` when the process is complete.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'suspend'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Unlock User',
        name: 'Okta: Unlock User',
        description:
            'Unlocks a user with a `LOCKED_OUT` status and returns them to `ACTIVE` status.  Users will be able to login with their current password.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'unlock'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Unsuspend User',
        name: 'Okta: Unsuspend User',
        description:
            'Unsuspends a user and returns them to the `ACTIVE` state.  This operation can only be performed on users that have a `SUSPENDED` status.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'lifecycle', 'unsuspend'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: List subscriptions of a User',
        name: 'Okta: List subscriptions of a User',
        description:
            'List subscriptions of a User. Only lists subscriptions for current user. An AccessDeniedException message is sent if requests are made from other users.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        _links: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        channels: { kind: 'array', subType: { kind: 'string' } },
                        notificationType: { kind: 'string' },
                        status: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'subscriptions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Get the subscription of a User with a specific notification type',
        name: 'Okta: Get the subscription of a User with a specific notification type',
        description:
            'Get the subscriptions of a User with a specific notification type. Only gets subscriptions for current user. An AccessDeniedException message is sent if requests are made from other users.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            notificationType: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    _links: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    channels: { kind: 'array', subType: { kind: 'string' } },
                    notificationType: { kind: 'string' },
                    status: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: ['', 'api', 'v1', 'users', input.inPorts.userId, 'subscriptions', input.inPorts.notificationType].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Subscribe to a specific notification type',
        name: 'Okta: Subscribe to a specific notification type',
        description:
            'Subscribes a User to a specific notification type. Only the current User can subscribe to a specific notification type. An AccessDeniedException message is sent if requests are made from other users.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            notificationType: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: [
                        '',
                        'api',
                        'v1',
                        'users',
                        input.inPorts.userId,
                        'subscriptions',
                        input.inPorts.notificationType,
                        'subscribe',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Okta: Unsubscribe from a specific notification type',
        name: 'Okta: Unsubscribe from a specific notification type',
        description:
            'Unsubscribes a User from a specific notification type. Only the current User can unsubscribe from a specific notification type. An AccessDeniedException message is sent if requests are made from other users.',
        category: 'Okta',
        color: '#00297a',
        integration: OktaIntegration,
        inPorts: {
            userId: { kind: 'string' },
            notificationType: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    url: [
                        '',
                        'api',
                        'v1',
                        'users',
                        input.inPorts.userId,
                        'subscriptions',
                        input.inPorts.notificationType,
                        'unsubscribe',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
];
